import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Rocket, ShieldCheck, Sparkles } from 'lucide-react';
import { Box, Flex, Text } from '@chakra-ui/react';

const CTABanner = () => {
  const navigate = useNavigate();
  return (
    <Box
      as="section"
      py={{ base: 16, md: 24 }}
      bg="transparent"
      fontFamily="body"
      overflow="hidden"
    >
      <Box maxW="1100px" mx="auto" px={{ base: 5, md: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Box
            borderRadius="2xl"
            bg="dark.secondary"
            p={{ base: 8, md: 16 }}
            textAlign="center"
            position="relative"
            overflow="hidden"
            border="1px solid"
            borderColor="dark.border"
            boxShadow="0 32px 64px rgba(11, 31, 58, 0.4)"
          >
            {/* Background elements */}
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              bg="radial-gradient(circle at top right, rgba(37, 99, 235, 0.2), transparent 50%), radial-gradient(circle at bottom left, rgba(34, 211, 238, 0.1), transparent 50%)"
              pointerEvents="none"
            />

            {/* Badge */}
            <Flex
              display="inline-flex"
              align="center"
              gap={2}
              px={3} py={1.5}
              borderRadius="full"
              bg="rgba(37, 99, 235, 0.15)"
              borderWidth="1px"
              borderColor="rgba(37, 99, 235, 0.3)"
              mb={8}
              position="relative"
              zIndex={1}
            >
              <Sparkles size={14} color="var(--chakra-colors-brand-cyan)" />
              <Text fontSize="xs" fontWeight="800" color="brand.cyan" letterSpacing="0.08em">
                START SYNCING
              </Text>
            </Flex>

            <Text
              as="h2"
              fontSize={{ base: 'xl', md: '4xl', lg: '5xl' }}
              fontWeight="900"
              color="white"
              letterSpacing="-0.03em"
              mb={4}
              position="relative"
              zIndex={1}
            >
              Start Syncing{' '}
              <Box as="span" color="rgba(255,255,255,0.6)">Jira and ClickUp Today.</Box>
            </Text>
            <Text
              fontSize="lg"
              color="rgba(255, 255, 255, 0.7)"
              maxW="600px"
              mx="auto"
              mb={10}
              lineHeight="1.75"
              position="relative"
              zIndex={1}
            >
              Keep Jira issues, ClickUp tasks, subtasks, and mapped fields aligned without rebuilding structure.{' '}
              <Box as="span" fontWeight="700" color="white">Get started in under 60 seconds.</Box>
            </Text>

            <Flex align="center" justify="center" gap={4} wrap="wrap" mb={10} position="relative" zIndex={1}>
              <Box
                as="button"
                display="inline-flex"
                alignItems="center"
                gap={3}
                px={10} py={4.5}
                borderRadius="xl"
                bg="brand.primary"
                color="white"
                fontWeight="800"
                fontSize="md"
                border="none"
                cursor="pointer"
                transition="all 200ms"
                boxShadow="0 0 20px rgba(34, 211, 238, 0.25)"
                _hover={{ 
                  transform: 'translateY(-2px)', 
                  boxShadow: '0 0 30px rgba(34, 211, 238, 0.4)', 
                  bg: 'brand.500' 
                }}
                style={{ fontFamily: 'inherit' }}
                onClick={() => navigate('/signup')}
              >
                Get Started for Free <Rocket size={20} strokeWidth={3} />
              </Box>
              <Box
                as="button"
                display="inline-flex"
                alignItems="center"
                px={8} py={4.5}
                borderRadius="xl"
                bg="rgba(255,255,255,0.15)"
                color="white"
                fontWeight="700"
                fontSize="md"
                border="1px solid rgba(255,255,255,0.35)"
                cursor="pointer"
                transition="all 200ms"
                _hover={{ bg: 'rgba(255,255,255,0.22)', borderColor: 'white' }}
                style={{ fontFamily: 'inherit' }}
                onClick={() => navigate('/login')}
              >
                Sign In
              </Box>
            </Flex>

            <Flex align="center" justify="center" gap={8} wrap="wrap" position="relative" zIndex={1}>
              {[
                'AES-256 Encryption Tunnel',
                'Priority Implementation Support',
                '99.9% Uptime Guarantee'
              ].map((item) => (
                <Flex key={item} align="center" gap={2}>
                  <ShieldCheck size={16} color="var(--chakra-colors-success-500)" />
                  <Text fontSize="xs" color="rgba(255,255,255,0.6)" fontWeight="600" letterSpacing="0.02em">{item.toUpperCase()}</Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        </motion.div>
      </Box>
      <style>{`
        @keyframes pulse-ring {
          0% { transform: translateX(-50%) translateY(0) scale(1); opacity: 0.05; }
          50% { transform: translateX(-50%) translateY(-20px) scale(1.1); opacity: 0.08; }
          100% { transform: translateX(-50%) translateY(0) scale(1); opacity: 0.05; }
        }
      `}</style>
    </Box>
  );
};

export default CTABanner;
